import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DatabaseConfigTab } from '@/components/databases/database-config-tab';
import { UpdateSchemaTab } from '@/components/databases/update-schema-tab';
import { EnvironmentSelector } from '@/components/shared/environment-selector';

// --- Types ---

type DatabaseTab = 'config' | 'update-schema';

const DATABASE_TABS: { key: DatabaseTab; label: string }[] = [
  { key: 'config', label: 'Configuration' },
  { key: 'update-schema', label: 'Update Schema' },
];

// --- Main Page ---

export function ProjectDatabasesPage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';

  const [activeTab, setActiveTab] = useState<DatabaseTab>('config');

  return (
    <main id="main-content" className="mx-auto max-w-5xl">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-content">Databases</h1>
        <p className="mt-1 text-sm text-content-secondary">
          Manage database configuration and run schema updates
        </p>
      </header>

      {/* Environment selector */}
      <div className="mb-4">
        <EnvironmentSelector projectKey={key} />
      </div>

      {/* Horizontal tab bar */}
      <nav aria-label="Database actions" className="mb-4">
        <div className="border-b border-border">
          <ul className="flex gap-0 -mb-px" role="tablist">
            {DATABASE_TABS.map((tab) => (
              <li key={tab.key} role="presentation">
                <button
                  role="tab"
                  id={'db-tab-' + tab.key}
                  aria-selected={activeTab === tab.key}
                  aria-controls={'db-panel-' + tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'inline-block px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2',
                    activeTab === tab.key
                      ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                      : 'border-transparent text-content-secondary hover:text-content hover:border-border',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Tab content */}
      <div
        id={'db-panel-' + activeTab}
        role="tabpanel"
        aria-labelledby={'db-tab-' + activeTab}
      >
        {activeTab === 'config' && <DatabaseConfigTab projectKey={key} />}
        {activeTab === 'update-schema' && <UpdateSchemaTab projectKey={key} />}
      </div>
    </main>
  );
}
